import * as React from 'react';
import { cn } from '@/lib/utils/cn';

/**
 * List and detail (T-0186): a list on the left, the selected item on the
 * right, read top to bottom. No overlays and no tabs inside tabs.
 *
 * Mirrors `.sx-list`, `.sx-item`, `.sx-gname`, `.sx-code`, `.sd`,
 * `.sd-head` and `.rq-empty-inline` in `docs/discovery/prototype/
 * discovery.css` (Sessions, and Findings after it).
 *
 *   <ListDetailLayout list={<ListPane head={…}>…items…</ListPane>}>
 *     <DetailHead … />
 *     …
 *   </ListDetailLayout>
 */

export function ListDetailLayout({
  list,
  children,
  detailKey,
  fill = false,
}: {
  list: React.ReactNode;
  children: React.ReactNode;
  /** Changing it scrolls the detail back to the top (a new item was picked). */
  detailKey?: string;
  /**
   * The detail fills the space and scrolls itself, for a table with its
   * toolbar (T-0188, the evidence ledger), instead of the reading column.
   */
  fill?: boolean;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    ref.current?.scrollTo?.({ top: 0 });
  }, [detailKey]);
  return (
    <div className="flex min-h-0 flex-1 border-t border-ink-100 bg-paper-0">
      {list}
      {fill ? (
        <div className="flex min-w-0 flex-1 flex-col">{children}</div>
      ) : (
        <div ref={ref} className="relative min-w-0 flex-1 overflow-y-auto">
          <div className="max-w-[980px] px-7 pb-10 pt-5">{children}</div>
        </div>
      )}
    </div>
  );
}

export function ListPane({
  label,
  head,
  foot,
  className,
  children,
}: {
  label: string;
  /** Controls above the scrolling list: mode switch, add button, filters. */
  head?: React.ReactNode;
  /** Links below the list, kept in view (T-0206: Add vendor, settings). */
  foot?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <nav
      aria-label={label}
      className={cn(
        'flex min-h-0 w-[300px] flex-none flex-col border-r border-ink-100 bg-paper-50 px-2.5 pt-3 max-[1280px]:w-[260px]',
        className,
      )}
    >
      {head ? <div className="flex flex-none flex-col gap-2.5 pb-1.5">{head}</div> : null}
      <div className="min-h-0 flex-1 overflow-y-auto pb-4">{children}</div>
      {foot ? (
        <div className="-mx-2.5 flex flex-none flex-col gap-0.5 border-t border-ink-100 bg-paper-50 px-2.5 pb-2.5 pt-2">
          {foot}
        </div>
      ) : null}
    </nav>
  );
}

export function ListGroupLabel({
  children,
  count,
  action,
}: {
  children: React.ReactNode;
  count?: number;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-2 px-1.5 pb-1.5 pt-3.5 text-[11px] font-semibold uppercase leading-[18px] tracking-[0.06em] text-ink-500">
      <span>{children}</span>
      {count !== undefined ? (
        <span className="font-mono font-normal tracking-normal">{count}</span>
      ) : null}
      {action ? <span className="ml-auto normal-case tracking-normal">{action}</span> : null}
    </div>
  );
}

export interface ListItemProps {
  selected?: boolean;
  onClick?: () => void;
  leading?: React.ReactNode;
  title: React.ReactNode;
  meta?: React.ReactNode;
  trailing?: React.ReactNode;
}

export function ListItem({ selected, onClick, leading, title, meta, trailing }: ListItemProps) {
  return (
    <button
      type="button"
      aria-current={selected ? 'true' : undefined}
      onClick={onClick}
      className={cn(
        'mb-0.5 flex w-full items-center gap-2.5 rounded-card border p-2 text-left text-body-sm text-ink-700',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
        selected
          ? 'border-ink-100 bg-paper-0 shadow-[inset_3px_0_0_var(--accent-amber-600)]'
          : 'border-transparent hover:bg-paper-100',
      )}
    >
      {leading}
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium text-ink-900">{title}</span>
        {meta ? <span className="block truncate text-meta text-ink-500">{meta}</span> : null}
      </span>
      {trailing}
    </button>
  );
}

/** What waits for review on a row (`.rv-count`), on the AI surface's tint (T-0206). */
export function ListCount({ children, title }: { children: React.ReactNode; title?: string }) {
  return (
    <span
      title={title}
      className="h-5 min-w-5 flex-none rounded-full border border-ai-surface-border bg-ai-surface px-1.5 text-center text-[11px] font-semibold leading-[18px] text-ai-ink"
    >
      {children}
    </span>
  );
}

/** An interview or questionnaire code (`.sx-code`): S3, Q-G. */
export function CodeBadge({ children, size = 'md' }: { children: string; size?: 'md' | 'lg' }) {
  return (
    <span
      className={cn(
        'inline-flex flex-none items-center justify-center rounded-chip border border-ink-100 bg-paper-100 px-1.5 font-mono font-semibold leading-none text-ink-900',
        size === 'lg' ? 'h-8 min-w-[44px] text-[14px]' : 'h-6 min-w-[36px] text-meta',
      )}
    >
      {children}
    </span>
  );
}

export function DetailHead({
  leading,
  title,
  meta,
  trailing,
}: {
  leading?: React.ReactNode;
  title: React.ReactNode;
  meta?: React.ReactNode;
  trailing?: React.ReactNode;
}) {
  return (
    <div className="mb-4 flex items-center gap-3.5">
      {leading}
      <div className="min-w-0 flex-1">
        <h2 className="text-h2 font-semibold text-ink-900">{title}</h2>
        {meta ? <div className="text-meta text-ink-500">{meta}</div> : null}
      </div>
      {trailing}
    </div>
  );
}

/** The bordered box under a detail's head (`.sd-meta`). */
export function DetailCard({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'mb-[18px] flex items-start gap-6 rounded-card border border-ink-100 bg-paper-0 px-4 py-3.5',
        className,
      )}
    >
      {children}
    </div>
  );
}

/** Small uppercase label above a group (`.p-label`). */
export function PartLabel({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'mb-1.5 text-[11px] font-medium uppercase leading-[18px] tracking-[0.06em] text-ink-500',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function DetailEmpty({ title, text }: { title: string; text: string }) {
  return (
    <div className="px-6 py-14 text-center text-body-sm text-ink-500">
      <strong className="mb-1 block text-[15px] font-semibold text-ink-900">{title}</strong>
      {text}
    </div>
  );
}

/**
 * A one-line record (`.done-row`): an interview of a person, a secure
 * link, a confirmed item, a department of the round. With `onClick` the
 * whole row is a button.
 */
export function RecordRow({
  icon,
  muted = false,
  onClick,
  children,
  className,
}: {
  icon?: React.ReactNode;
  muted?: boolean;
  onClick?: () => void;
  children: React.ReactNode;
  className?: string;
}) {
  const cls = cn(
    'mb-1 flex w-full items-center gap-2.5 rounded-card border border-ink-100 bg-paper-0 px-3 py-[9px] text-left text-body-sm',
    muted ? 'text-ink-500' : 'text-ink-900',
    onClick &&
      'hover:border-ink-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
    className,
  );
  const mark = icon ? (
    <span
      aria-hidden="true"
      className={cn('flex flex-none', muted ? 'text-ink-300' : 'text-posture-healthy')}
    >
      {icon}
    </span>
  ) : null;
  return onClick ? (
    <button type="button" onClick={onClick} className={cls}>
      {mark}
      {children}
    </button>
  ) : (
    <div className={cls}>
      {mark}
      {children}
    </div>
  );
}

/** Underlined text button (`.linkish`, `.linkish.danger`). */
export const linkishClass = (danger = false) =>
  cn(
    'inline-flex items-center gap-1 rounded-chip text-meta font-medium underline underline-offset-[3px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600 disabled:cursor-not-allowed disabled:opacity-60',
    danger ? 'text-posture-critical' : 'text-ink-700 hover:text-ink-900',
  );
